import React from "react";
import styles from "./skeleton.module.css"; // Стили вынесены в отдельный файл

type SkeletonProps = {
  /**
   * Тип элемента заглушки
   * @default 'rectangle'
   */
  variant?: "rectangle" | "circle" | "text";

  /**
   * Ширина заглушки
   * @default '100%'
   */
  width?: number | string;

  /**
   * Высота заглушки
   * @default '1em'
   */
  height?: number | string;

  /**
   * Количество повторяющихся элементов (для текста)
   * @default 1
   */
  count?: number;

  /**
   * Включить/выключить анимацию пульсации
   * @default true
   */
  animated?: boolean;

  /**
   * Пользовательский класс для стилизации
   */
  className?: string;

  /**
   * Инлайн стили
   */
  style?: React.CSSProperties;
};

const Skeleton: React.FC<SkeletonProps> = ({
  variant = "rectangle",
  width = "100%",
  height = "1em",
  count = 1,
  animated = true,
  className = "",
  style = {},
}) => {
  // Нормализация размеров
  const normalizedWidth = typeof width === "number" ? `${width}px` : width;
  const normalizedHeight = typeof height === "number" ? `${height}px` : height;

  // Базовые классы
  const baseClass = `${styles.skeleton} ${styles[`skeleton--${variant}`]}`;
  const animationClass = animated ? styles["skeleton--animated"] : "";
  const fullClassName = `${baseClass} ${animationClass} ${className}`.trim();

  // Стили для элемента
  const elementStyle = {
    width: normalizedWidth,
    height: normalizedHeight,
    ...style,
  };

  // Для текстовых элементов генерируем несколько строк
  if (variant === "text" && count > 1) {
    return (
      <div className={styles["skeleton-text-group"]}>
        {[...Array(count)].map((_, index) => (
          <div
            key={index}
            className={fullClassName}
            style={{
              ...elementStyle,
              // Последняя строка текста может быть короче
              width: index === count - 1 ? "80%" : normalizedWidth,
            }}
          />
        ))}
      </div>
    );
  }

  return <div className={fullClassName} style={elementStyle} />;
};

export default Skeleton;
